import React, { useEffect, useState } from "react";
import { Table, Button, Space, Modal, Form, Input, Card, Select } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const { Option } = Select;

const BookPublisherManagement = () => {
  const navigate = useNavigate();
  const [publishers, setPublishers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPublisher, setEditingPublisher] = useState(null);
  const [form] = Form.useForm();
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    fetchPublishers();
    fetchCompanies();
  }, []);

  const fetchPublishers = async () => {
    setLoading(true);
    try {
      const response = await axios.get("super-admin/book-publishers");
      setPublishers(response.data);
    } catch (error) {
      toast.error("Failed to fetch book publishers");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get("super-admin/companies");
      setCompanies(response.data);
    } catch (error) {
      console.error("Failed to fetch companies:", error);
    }
  };

  const handleCreate = () => {
    setEditingPublisher(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingPublisher(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (record) => {
    try {
      await axios.delete(`super-admin/book-publishers/${record.id}`);
      toast.success("Book publisher deleted successfully");
      fetchPublishers();
    } catch (error) {
      toast.error("Failed to delete book publisher");
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingPublisher) {
        await axios.put(`super-admin/book-publishers/${editingPublisher.id}`, values);
        toast.success("Book publisher updated successfully");
      } else {
        await axios.post("super-admin/book-publishers", values);
        toast.success("Book publisher created successfully");
      }
      setModalVisible(false);
      form.resetFields();
      fetchPublishers();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save book publisher");
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Company",
      dataIndex: ["company", "company_name"],
      key: "company",
      render: (text) => text || "N/A",
    },
    {
      title: "Products Count",
      dataIndex: ["_count", "product"],
      key: "productCount",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Button danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Button 
        icon={<ArrowLeftOutlined />} 
        onClick={() => navigate("/super-admin/dashboard")}
        style={{ marginBottom: "16px" }}
      >
        Back to Dashboard
      </Button>
      <Card
        title="Book Publisher Management"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Create Book Publisher
          </Button>
        }
      >
        <Table
          dataSource={publishers}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingPublisher ? "Edit Book Publisher" : "Create Book Publisher"}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Publisher Name"
            rules={[{ required: true, message: "Please input publisher name!" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="company_id"
            label="Company"
            rules={[{ required: true, message: "Please select company!" }]}
          >
            <Select placeholder="Select Company" allowClear>
              {companies.map((company) => (
                <Option key={company.id} value={company.id}>
                  {company.company_name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BookPublisherManagement;
